import { Component, signal, ElementRef, ViewChild, AfterViewInit } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { CommonModule, NgOptimizedImage } from '@angular/common';
import { AuthService } from '../../core/service/auth.service';
import { LoginService } from './service/login.service';
import { Router, RouterModule } from '@angular/router';
import { finalize } from 'rxjs/operators';
import { NotificationsService } from '../../shared/components/notifications/service/notifications.service';
import { HomeButtonComponent } from "../../shared/components/home-button/home-button.component";
import { ResendEmailVerificationComponent } from './components/resend-email-verification/resend-email-verification.component';
import { ConfettiService } from '../../shared/confetti';
import { gsap } from 'gsap';

@Component({
  selector: 'app-login',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, RouterModule, HomeButtonComponent, ResendEmailVerificationComponent,
    NgOptimizedImage
  ],
  templateUrl: './login.component.html',
  styleUrl: './login.component.css',
})
export class LoginComponent implements AfterViewInit {
  loginForm: FormGroup;
  isLoading = false;
  errorMessage = '';
  showEmailVerificationModal = false;
  showPassword = signal(false);

  @ViewChild('pageContainer') pageContainer!: ElementRef<HTMLDivElement>;
  @ViewChild('cardContainer') cardContainer!: ElementRef<HTMLDivElement>;
  @ViewChild('loginButton') loginButton!: ElementRef<HTMLButtonElement>;

  constructor(
    private fb: FormBuilder,
    private authService: AuthService,
    private loginService: LoginService,
    private router: Router,
    private notificationsService: NotificationsService,
    private conffeti: ConfettiService

  ) {
    this.loginForm = this.fb.group({
      email: ['', [Validators.required, Validators.email]],
      password: ['', [Validators.required, Validators.minLength(6)]],
      rememberMe: [false]
    });
  }

  ngAfterViewInit(): void {
    // Fade in del contenedor principal
    if (this.pageContainer?.nativeElement) {
      gsap.from(this.pageContainer.nativeElement, {
        opacity: 0,
        duration: 0.6,
        ease: 'power1.out'
      });
    }

    // Animación de los campos del formulario con efecto lateral
    if (this.cardContainer?.nativeElement) {
      const fields = this.cardContainer.nativeElement.querySelectorAll('.form-field');
      if (fields && fields.length) {
        gsap.set(fields, { opacity: 0, x: -30 });
        gsap.to(fields, {
          opacity: 1,
          x: 0,
          stagger: 0.1,
          duration: 0.5,
          ease: 'back.out(1.7)'
        });
      }

      // Animación para los enlaces
      const links = this.cardContainer.nativeElement.querySelectorAll('a');
      if (links && links.length) {
        gsap.from(links, {
          opacity: 0,
          scale: 0.8,
          stagger: 0.15,
          duration: 0.4,
          ease: 'back.out(1.7)'
        });
      }
    }
  }

  onSubmit(): void {
    if (this.loginForm.valid) {
      this.isLoading = true;
      this.errorMessage = '';

      const { email, password } = this.loginForm.value;

      this.loginService.login(email, password)
        .pipe(
          finalize(() => {
            // console.log('Login request completed');
            this.isLoading = false;
          })
        )
        .subscribe({
          next: (response) => {
            // console.log('Login response:', response);
            if (response.status && response.data) {
              // Update auth service with user data
              this.authService.setToken(response.data.token);
              this.authService.setUser(response.data.user);
              this.conffeti.fromSides(
                {
                  particleCount: 100,
                  spread: 100,
                  startVelocity: 30,
                  gravity: 1,
                  drift: 10,

                }
              );
              // Show success notification
              this.notificationsService.addNotification({
                message: 'Inicio de sesión exitoso',
                type: 'success',
                duration: 3000
              });

              // Pequeño retraso antes de navegar para permitir que los cambios se propaguen
              setTimeout(() => {
                // Navigate to home page or dashboard
                this.router.navigate(['/']);
              }, 100);
            }
          },
          error: (error) => {
            // console.log(error);
            let error_codes = [401]
            if (error_codes.includes(error.status) && error.error.message === "Cuenta no verificada. Por favor verifique su correo.") {
              this.notificationsService.addNotification({
                message: error.error.message,
                type: "error",
                duration: 5000
              });


              this.showEmailVerificationModal = true;

            }

              if (error_codes.includes(error.status) ) {
              this.notificationsService.addNotification({
                message: error.error.message,
                type: "error",
                duration: 5000
              });
            }

          }
        });
    }
  }

  // Métodos para la animación del botón
  onButtonMouseEnter() {
    if (this.loginButton?.nativeElement) {
      gsap.to(this.loginButton.nativeElement, {
        scale: 1.05,
        y: -3,
        boxShadow: '0 10px 15px rgba(0, 0, 0, 0.15)',
        duration: 0.3,
        ease: 'back.out(1.7)'
      });
    }
  }

  onButtonMouseLeave() {
    if (this.loginButton?.nativeElement) {
      gsap.to(this.loginButton.nativeElement, {
        scale: 1,
        y: 0,
        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
        duration: 0.3,
        ease: 'back.out(1.7)'
      });
    }
  }

  onCloseEmailVerification() {
    this.showEmailVerificationModal = false;
  }

  toggleShowPassword() {
    this.showPassword.set(!this.showPassword());
  }

  get email() { return this.loginForm.get('email'); }
  get password() { return this.loginForm.get('password'); }
}
